/**
 * Planning Agent intake snapshots — non-destructive Restart.
 *
 * Every write to `content_brief_plans.intake_state` snapshots the prior
 * value into `content_brief_plan_snapshots` first (edge function does
 * this on model turns; `snapshotAndReset` below does it for the client
 * Restart button). Users can list + restore snapshots from the Planning
 * rail so a Restart is always reversible.
 */
import { supabase } from "@/integrations/supabase/client";

export type PlanningSnapshot = {
  id: string;
  plan_id: string;
  workspace_id: string;
  brief_id: string;
  intake_state: Record<string, unknown> | null;
  brief_summary: string | null;
  planning_notes: string | null;
  reason: string;
  created_at: string;
};

const EMPTY_INTAKE = { transcript: [], checklist: null, approved_brief: null };

export async function listPlanSnapshots(
  planId: string,
  limit = 20,
): Promise<PlanningSnapshot[]> {
  const { data, error } = await supabase
    .from("content_brief_plan_snapshots" as never)
    .select("id, plan_id, workspace_id, brief_id, intake_state, brief_summary, planning_notes, reason, created_at")
    .eq("plan_id", planId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as unknown as PlanningSnapshot[];
}

/** Snapshot current intake_state, then reset the plan row to empty. */
export async function snapshotAndReset(planId: string, reason: "restart" = "restart"): Promise<void> {
  const { data: row, error: readErr } = await supabase
    .from("content_brief_plans")
    .select("id, workspace_id, brief_id, intake_state, brief_summary, planning_notes")
    .eq("id", planId)
    .maybeSingle();
  if (readErr) throw new Error(`Failed to read plan: ${readErr.message}`);
  if (!row) throw new Error("Plan row not found");

  const prior = (row.intake_state ?? null) as {
    transcript?: unknown[];
    approved_brief?: unknown;
    checklist?: unknown;
  } | null;
  const priorHasContent = Boolean(
    (Array.isArray(prior?.transcript) && prior!.transcript!.length > 0) ||
      prior?.approved_brief ||
      prior?.checklist,
  );

  if (priorHasContent) {
    const { error: snapErr } = await supabase
      .from("content_brief_plan_snapshots" as never)
      .insert({
        plan_id: row.id,
        workspace_id: row.workspace_id,
        brief_id: row.brief_id,
        intake_state: (prior ?? {}) as unknown as Record<string, unknown>,
        brief_summary: row.brief_summary,
        planning_notes: row.planning_notes,
        reason,
      } as never);
    if (snapErr) throw new Error(`Failed to snapshot planning session: ${snapErr.message}`);
  }

  const { error: updErr } = await supabase
    .from("content_brief_plans")
    .update({
      intake_state: EMPTY_INTAKE,
      brief_summary: null,
      planning_notes: null,
      status: "running",
      error_message: null,
      completed_at: null,
    })
    .eq("id", row.id);
  if (updErr) throw new Error(`Failed to reset planning session: ${updErr.message}`);
}

/** Snapshot current state, then write the snapshot's intake_state back onto the plan row. */
export async function restorePlanSnapshot(snapshotId: string): Promise<void> {
  const { data: snap, error: snapErr } = await supabase
    .from("content_brief_plan_snapshots" as never)
    .select("id, plan_id, workspace_id, brief_id, intake_state, brief_summary, planning_notes")
    .eq("id", snapshotId)
    .maybeSingle();
  if (snapErr) throw snapErr;
  if (!snap) throw new Error("Snapshot not found");
  const s = snap as unknown as PlanningSnapshot;

  const { data: row, error: readErr } = await supabase
    .from("content_brief_plans")
    .select("id, workspace_id, brief_id, intake_state, brief_summary, planning_notes")
    .eq("id", s.plan_id)
    .maybeSingle();
  if (readErr) throw readErr;
  if (!row) throw new Error("Plan row not found");

  // Snapshot current state first so restore itself is reversible.
  const prior = (row.intake_state ?? null) as {
    transcript?: unknown[];
    approved_brief?: unknown;
    checklist?: unknown;
  } | null;
  const priorHasContent = Boolean(
    (Array.isArray(prior?.transcript) && prior!.transcript!.length > 0) ||
      prior?.approved_brief ||
      prior?.checklist,
  );
  if (priorHasContent) {
    await supabase
      .from("content_brief_plan_snapshots" as never)
      .insert({
        plan_id: row.id,
        workspace_id: row.workspace_id,
        brief_id: row.brief_id,
        intake_state: (prior ?? {}) as unknown as Record<string, unknown>,
        brief_summary: row.brief_summary,
        planning_notes: row.planning_notes,
        reason: "pre_restore",
      } as never);
  }

  const { error: updErr } = await supabase
    .from("content_brief_plans")
    .update({
      intake_state: (s.intake_state ?? EMPTY_INTAKE) as never,
      brief_summary: s.brief_summary,
      planning_notes: s.planning_notes,
      status: "running",
      error_message: null,
      completed_at: null,
    })
    .eq("id", row.id);
  if (updErr) throw updErr;
}

/** Short summary of a snapshot for the restore picker. */
export function summarizeSnapshot(snap: PlanningSnapshot): { turnCount: number; hasProposed: boolean } {
  const st = snap.intake_state as { transcript?: unknown[]; approved_brief?: unknown } | null;
  const transcript = Array.isArray(st?.transcript) ? st!.transcript! : [];
  const turnCount = transcript.filter((m) => {
    const role = (m as { role?: string })?.role;
    return role === "user" || role === "assistant";
  }).length;
  return { turnCount, hasProposed: Boolean(st?.approved_brief) };
}
